import { Clock3, Film, RotateCcw, Sparkles } from 'lucide-react'
import { useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { cinemaAssets } from '../config/assets'
import { loadQuizPage } from '../loaders/quizPage'
import { loadResultPage } from '../loaders/resultPage'
import { preloadResultEngine } from '../services/resultEngine'
import { useSession } from '../state/session-context'

function warmQuizFlow() {
  return Promise.all([
    loadQuizPage(),
    loadResultPage(),
    preloadResultEngine(),
  ]).then(() => undefined)
}

export function HomePage() {
  const navigate = useNavigate()
  const { answeredCount, currentQuestion, isComplete, restart } = useSession()
  const hasProgress = answeredCount > 0

  useEffect(() => {
    // Do not wait for the decorative hero images to finish. On weak mobile
    // networks that may take tens of seconds, while the user can already start
    // answering. Warm the interactive flow as soon as the readable home screen
    // is mounted.
    const timeout = window.setTimeout(() => void warmQuizFlow(), 600)
    return () => {
      window.clearTimeout(timeout)
    }
  }, [])

  const start = () => {
    if (isComplete) navigate('/result')
    else navigate(`/quiz/${hasProgress ? Math.min(currentQuestion + 1, 28) : 1}`)
  }
  const restartFromHome = () => {
    restart()
    navigate('/quiz/1')
  }

  return (
    <main className="neutral-page home-page cinema-cover-page">
      <div className="cinema-page-art" aria-hidden="true">
        <img className="cinema-paper-underlay cinema-paper-underlay--home" src={cinemaAssets.homePaper} width="941" height="1671" alt="" decoding="async" fetchPriority="low" />
        <img className="cinema-top-film" src={cinemaAssets.topFilm} width="1000" height="350" alt="" decoding="async" fetchPriority="low" />
        <img className="cinema-stamp" src={cinemaAssets.stamp} width="360" height="240" alt="" decoding="async" fetchPriority="low" />
      </div>
      <section className="page-content home-content home-content--replica">
        <h1 className="visually-hidden">如果你的人生是一部电影</h1>
        <img
          className="cinema-title-art cinema-title-art--home"
          src={cinemaAssets.homeTitle}
          width="640"
          height="326"
          alt=""
          aria-hidden="true"
          decoding="sync"
          fetchPriority="high"
        />
        <p className="home-intro">
          你的选择、关系、情绪和那些说不清的小习惯，<br />可能早就在某部电影里出现过。
        </p>
        <div className="home-facts">
          <span><Film size={22} />28道小场景</span>
          <span><Clock3 size={22} />约5分钟</span>
          <span><Sparkles size={22} />没有标准答案</span>
        </div>
        <img
          className="cinema-hero-film cinema-hero-film--home"
          src={cinemaAssets.homeHero}
          width="760"
          height="506"
          alt=""
          aria-hidden="true"
          decoding="async"
          fetchPriority="auto"
        />
        <p className="home-tagline">看看哪一部电影，<br />最像正在生活里的你。</p>
        <button
          className="primary-button"
          onClick={start}
          onFocus={() => void warmQuizFlow()}
          onPointerDown={() => void warmQuizFlow()}
          onPointerEnter={() => void warmQuizFlow()}
        >
          {isComplete
            ? '查看上次测试结果'
            : hasProgress
              ? `继续答题 · ${answeredCount}/28`
              : '开始匹配我的灵魂电影'}
          <Sparkles size={20} />
        </button>
        {hasProgress ? (
          <button className="text-button" onClick={restartFromHome}>
            <RotateCcw size={16} /> 重新开始
          </button>
        ) : (
          <p className="home-footnote">— 答完即可解锁你的专属结果 —</p>
        )}
      </section>
    </main>
  )
}
